"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import {
  BarChart3,
  BookOpen,
  Clock,
  Home,
  Key,
  Library,
  MessageSquare,
  Play,
  Settings2,
  ShieldCheck,
  Terminal,
  User,
} from "lucide-react";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { listPipelineRuns } from "@/lib/api";
import { governancePageTitle } from "@/lib/governance-navigation";
import { useWorkspace } from "@/contexts/workspace-context";
import { useAuth } from "@/contexts/auth-context";

interface RecentRun {
  job_id: string;
  query?: string;
  user_query?: string;
}

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type PageItem = {
  label: string;
  icon: typeof Home;
  path: string;
  capabilities?: string[];
  requiresWorkspace?: boolean;
};

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const router = useRouter();
  const locale = useLocale();
  const t = useTranslations("nav");
  const tCommon = useTranslations("common");
  const { hasCapability } = useAuth();
  const { selected, workspaces, loading: workspaceLoading } = useWorkspace();
  const [recentRuns, setRecentRuns] = useState<RecentRun[]>([]);

  useEffect(() => {
    let current = true;
    setRecentRuns([]);
    if (!open || !selected || !hasCapability("runs.view_own") && !hasCapability("runs.view_all")) return;
    listPipelineRuns(0, 5, selected).then((response) => {
      if (current && response.ok && response.data?.items) setRecentRuns(response.data.items);
    }).catch(() => {});
    return () => { current = false; };
  }, [open, selected, hasCapability]);

  const navigate = (path: string) => {
    router.push(`/${locale}${path}`);
    onOpenChange(false);
  };

  const pages: PageItem[] = [
    { label: t("home"), icon: Home, path: "" },
    {
      label: t("runs"),
      icon: Play,
      path: "/runs",
      capabilities: ["runs.create", "runs.view_own", "runs.view_all"],
      requiresWorkspace: true,
    },
    {
      label: t("analytics"),
      icon: BarChart3,
      path: "/analytics",
      capabilities: ["analytics.view_own", "analytics.view_platform"],
      requiresWorkspace: true,
    },
    {
      label: t("library"),
      icon: Library,
      path: "/library",
      capabilities: ["prompts.view", "configs.view"],
      requiresWorkspace: true,
    },
    {
      label: governancePageTitle("/prompt-governance", locale) || t("prompts"),
      icon: MessageSquare,
      path: "/prompt-governance",
      capabilities: ["prompts.view"],
      requiresWorkspace: true,
    },
    {
      label: governancePageTitle("/config-governance", locale) || t("configs"),
      icon: Settings2,
      path: "/config-governance",
      capabilities: ["configs.view"],
      requiresWorkspace: true,
    },
    {
      label: governancePageTitle("/content-safety", locale) || t("home"),
      icon: ShieldCheck,
      path: "/content-safety",
      capabilities: ["content_safety.read", "content_safety.manage"],
      requiresWorkspace: true,
    },
    {
      label: governancePageTitle("/feedback-curation", locale) || t("home"),
      icon: BookOpen,
      path: "/feedback-curation",
      capabilities: ["feedback.read", "feedback.curate"],
      requiresWorkspace: true,
    },
    {
      label: t("logs"),
      icon: Terminal,
      path: "/logs",
      capabilities: ["logs.view_own", "logs.view_all"],
      requiresWorkspace: true,
    },
    {
      label: tCommon("apiKeys"),
      icon: Key,
      path: "/api-keys",
      capabilities: ["api_keys.manage_own"],
      requiresWorkspace: true,
    },
    { label: tCommon("profile"), icon: User, path: "/profile" },
  ].filter((page) => {
    if (page.capabilities && !page.capabilities.some(hasCapability)) return false;
    if (page.requiresWorkspace && !workspaceLoading && workspaces.length === 0) return false;
    return true;
  });

  const copy = locale === "ar"
    ? { search: "البحث في الصفحات والعمليات…", empty: "لا توجد نتائج.", pages: "الصفحات", recent: "عمليات حديثة" }
    : { search: "Search pages and runs…", empty: "No results found.", pages: "Pages", recent: "Recent runs" };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}
      title={tCommon("commandPalette")} description={tCommon("commandPaletteHelp")}>
      <CommandInput placeholder={copy.search} />
      <CommandList>
        <CommandEmpty>{copy.empty}</CommandEmpty>
        <CommandGroup heading={copy.pages}>
          {pages.map((page) => (
            <CommandItem key={page.path} onSelect={() => navigate(page.path)} className="gap-2">
              <page.icon className="h-4 w-4 text-muted-foreground" />
              {page.label}
            </CommandItem>
          ))}
        </CommandGroup>

        {recentRuns.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading={copy.recent}>
              {recentRuns.map((run) => (
                <CommandItem
                  key={run.job_id}
                  onSelect={() => navigate(`/runs/${run.job_id}`)}
                  className="gap-2"
                >
                  <Clock className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="truncate">{run.query || run.user_query || run.job_id}</span>
                  <span className="ms-auto shrink-0 font-mono text-[10px] text-muted-foreground">
                    {run.job_id.slice(0, 8)}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}
